import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit2 } from "iconsax-react";
import { Button, DatePicker, Input, ModalForm, Skeleton, Toast, Prompt } from "@/shared/ui";
import { formatDateToISO, extractErrorMessage, type Locale } from "@/shared/utils";
import { editLeaveSchema } from "@/features/hr/leave-applications/validation";
import type { CreateLeaveFormValues } from "@/features/hr/leave-applications/validation";
import AnnualLeavePreview from "@/features/hr/leave-applications/components/AnnualLeavePreview";
import UnpaidLeavePreview from "@/features/hr/leave-applications/components/UnpaidLeavePreview";
import MedicalLeavePreview from "@/features/hr/leave-applications/components/MedicalLeavePreview";
import type { LeaveType } from "@/features/hr/leave-applications";
import type { SelfServiceRequest } from "../types";
import {
  useUpdateSelfAnnualLeaveMutation,
  useUpdateSelfUnpaidLeaveMutation,
  useUpdateSelfMedicalLeaveMutation,
} from "../api";
import { useSelfLeaveRequests } from "../hooks/useSelfLeaveRequests";

interface Props {
  request: SelfServiceRequest;
  onClose: () => void;
  onSuccess: () => void;
}

function selfTypeToLeaveType(type: string): LeaveType {
  if (type === "annual_leave") return "annual";
  if (type === "medical_leave") return "medical";
  return "unpaid";
}

export default function EditSelfLeaveForm({ request, onClose, onSuccess }: Props) {
  const { i18n, t } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const { employeeLike, isProfileLoading } = useSelfLeaveRequests();

  const leaveType = selfTypeToLeaveType(request.type);

  const [showPreview, setShowPreview] = useState(false);
  const [formDataForPreview, setFormDataForPreview] = useState<CreateLeaveFormValues | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const [updateAnnual, { isLoading: isUpdatingAnnual }] = useUpdateSelfAnnualLeaveMutation();
  const [updateUnpaid, { isLoading: isUpdatingUnpaid }] = useUpdateSelfUnpaidLeaveMutation();
  const [updateMedical, { isLoading: isUpdatingMedical }] = useUpdateSelfMedicalLeaveMutation();

  const isLoading = isUpdatingAnnual || isUpdatingUnpaid || isUpdatingMedical;

  const schema = useMemo(
    function () {
      return editLeaveSchema(leaveType);
    },
    [leaveType],
  );

  type EditFormValues = CreateLeaveFormValues;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      start_date: request.date_range?.start_date ? new Date(request.date_range.start_date + "T00:00:00") : undefined,
      end_date: request.date_range?.end_date ? new Date(request.date_range.end_date + "T00:00:00") : undefined,
      reason: request.details?.reason || "",
      diagnosis: request.details?.diagnosis || "",
      approval_resolution: null,
    },
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  function handleStartDateChange(v: Date | null) {
    if (v) {
      setValue("start_date", v, { shouldValidate: true });
    } else {
      setValue("start_date", undefined as any, { shouldValidate: true });
    }
  }

  function handleEndDateChange(v: Date | null) {
    if (v) {
      setValue("end_date", v, { shouldValidate: true });
    } else {
      setValue("end_date", undefined as any, { shouldValidate: true });
    }
  }

  function onSubmit(data: EditFormValues) {
    setFormDataForPreview(data);
    setShowPreview(true);
  }

  function handlePreviewClose() {
    setShowPreview(false);
  }

  async function handlePreviewSubmit() {
    if (!formDataForPreview || isLoading) return;

    try {
      const data = formDataForPreview;
      const startDateISO = formatDateToISO(data.start_date!);
      const endDateISO = formatDateToISO(data.end_date!);

      if (leaveType === "annual") {
        await updateAnnual({
          id: request.id,
          data: { start_date: startDateISO, end_date: endDateISO, reason: data.reason || undefined },
        }).unwrap();
      } else if (leaveType === "unpaid") {
        await updateUnpaid({
          id: request.id,
          data: { start_date: startDateISO, end_date: endDateISO, reason: data.reason || undefined },
        }).unwrap();
      } else {
        await updateMedical({
          id: request.id,
          data: { start_date: startDateISO, end_date: endDateISO, diagnosis: data.diagnosis || undefined },
        }).unwrap();
      }

      setPrompt({
        title: t("leaveForm.editSuccessTitle"),
        text: t("leaveForm.editSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("leaveForm.editErrorText"),
        text: extractErrorMessage(err, "leaveForm.editErrorText", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    setPrompt(null);
    if (prompt?.variant === "success") {
      onSuccess();
    }
  }

  if (prompt) {
    return (
      <Prompt
        variant={prompt.variant}
        onClose={handlePromptClose}
        title={prompt.title}
        text={prompt.text}
        namespace="EmployeePortal"
      />
    );
  }

  if (showPreview && formDataForPreview && employeeLike) {
    if (leaveType === "annual") {
      return (
        <AnnualLeavePreview
          employee={employeeLike}
          formData={formDataForPreview}
          onClose={handlePreviewClose}
          onSubmit={handlePreviewSubmit}
          isLoading={isLoading}
        />
      );
    }
    if (leaveType === "unpaid") {
      return (
        <UnpaidLeavePreview
          employee={employeeLike}
          formData={formDataForPreview}
          onClose={handlePreviewClose}
          onSubmit={handlePreviewSubmit}
          isLoading={isLoading}
        />
      );
    }
    return (
      <MedicalLeavePreview
        employee={employeeLike}
        formData={{ ...formDataForPreview, diagnosis: formDataForPreview.diagnosis }}
        onClose={handlePreviewClose}
        onSubmit={handlePreviewSubmit}
        isLoading={isLoading}
      />
    );
  }

  return (
    <ModalForm
      icon={Edit2}
      onClose={onClose}
      footer={!isProfileLoading && employeeLike ? (
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          <Button variant="secondary" size="huge" onClick={onClose} type="button">
            {t("leaveForm.cancel")}
          </Button>
          <Button variant="primary" size="huge" type="submit" form="edit-self-leave-form" disabled={isLoading}>
            {isLoading ? t("leaveForm.saving") : t("leaveForm.continue")}
          </Button>
        </div>
      ) : undefined}
    >
      {isProfileLoading || !employeeLike ? (
        <div className="flex flex-col h-full gap-5 py-5">
          <Skeleton height={28} borderRadius={8} />
          <Skeleton height={16} width={200} borderRadius={8} />
          <Skeleton height={42} borderRadius={12} />
          <Skeleton height={42} borderRadius={12} />
        </div>
      ) : (
      <form id="edit-self-leave-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("leaveForm.editTitle")}</h4>
          <p className="text-body-regular-sm content-base-secondary">{t("leaveForm.editSubtitle")}</p>
        </div>

        <div className="flex-1 overflow-auto flex flex-col gap-4 py-5 px-1">
          {request.application_review_note && (
            <Toast
              color="notice"
              text={`${t("leaveForm.revisionNotePrefix")}: ${request.application_review_note}`}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}

          <DatePicker
            label={t("leaveForm.start_date.label")}
            hasRequiredLabel
            placeholder={t("leaveForm.start_date.placeholder")}
            value={startDate || null}
            onChange={handleStartDateChange}
            locale={locale}
            error={errors.start_date?.message ? t(errors.start_date.message as string) : undefined}
          />

          <DatePicker
            label={t("leaveForm.end_date.label")}
            hasRequiredLabel
            placeholder={t("leaveForm.end_date.placeholder")}
            value={endDate || null}
            onChange={handleEndDateChange}
            locale={locale}
            error={errors.end_date?.message ? t(errors.end_date.message as string) : undefined}
          />

          {leaveType === "unpaid" && (
            <Input
              label={t("leaveForm.reason.label")}
              placeholder={t("leaveForm.reason.placeholder")}
              isTextarea
              {...register("reason")}
              error={errors.reason?.message ? t(errors.reason.message as string) : undefined}
            />
          )}

          {leaveType === "medical" && (
            <Input
              label={t("leaveForm.diagnosis.label")}
              placeholder={t("leaveForm.diagnosis.placeholder")}
              isTextarea
              {...register("diagnosis")}
              error={errors.diagnosis?.message ? t(errors.diagnosis.message as string) : undefined}
            />
          )}
        </div>

      </form>
      )}
    </ModalForm>
  );
}
